import {  Component,  ElementRef,  Input,  ViewChild,  OnChanges,  SimpleChanges,  OnDestroy,  AfterViewInit } from '@angular/core';

type WowzaDirection = 'play' | 'publish';

interface WowzaStreamInfo {
  applicationName: string;
  streamName: string;
  sessionId?: string;
}

interface WowzaSignalRequest {
  direction: WowzaDirection;
  command: 'getOffer' | 'sendResponse';
  streamInfo: WowzaStreamInfo;
  userData?: any;
  secureToken?: any; // optional JSON object passed through "as-is"
  sdp?: RTCSessionDescriptionInit;
}

interface WowzaSignalResponse {
  status: number;
  statusDescription?: string;
  direction?: WowzaDirection;
  command?: string;
  streamInfo?: WowzaStreamInfo;
  sdp?: RTCSessionDescriptionInit;
  iceCandidates?: Array<{
    candidate: string;
    sdpMid?: string;
    sdpMLineIndex?: number;
    usernameFragment?: string;
  }>;
}

@Component({
  selector: 'app-wowza-webrtc-player',
  template: `
    <video
      #video
      autoplay
      playsinline
      controls
      [muted]="muted"
      style="width: 100%; height: auto; background: #222; border-radius: 8px;"
    ></video>
  `,
  
  styleUrls: ['./wowza-webrtc-player.component.scss']
})
export class WowzaWebrtcPlayerComponent
  implements AfterViewInit, OnChanges, OnDestroy
{
  /**
   * Inputs (required)
   */
  @Input() signalingUrl!: string;      // e.g. wss://Video02.noacam.net:443/webrtc-session.json
  @Input() applicationName!: string;   // e.g. live2
  @Input() streamName!: string;        // e.g. CAM5001.stream

  /**
   * Optional secure token object.
   * If provided, it is included in the getOffer payload under "secureToken".
   */
  @Input() secureToken?: any;

  /**
   * Optional: many browsers require video to be muted to autoplay without user gesture.
   * You can set muted=false if you want audio and you handle autoplay policies.
   */
  @Input() muted: boolean = true;

  @ViewChild('video', { static: true }) videoRef!: ElementRef<HTMLVideoElement>;

  private ws: WebSocket | null = null;
  private pc: RTCPeerConnection | null = null;
  private started = false;
  private restarting = false;

  ngAfterViewInit(): void {
    this.tryStart();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // If inputs change after start, restart the session.
    const affectsConnection =
      'signalingUrl' in changes ||
      'applicationName' in changes ||
      'streamName' in changes ||
      'secureToken' in changes;

    if (affectsConnection) {
      // AfterViewInit might not yet have run.
      if (this.videoRef?.nativeElement) {
        this.tryRestart();
      }
    }
  }

  ngOnDestroy(): void {
    this.stop();
  }

  private tryStart(): void {
    if (this.started) return;
    if (!this.videoRef?.nativeElement) return;

    if (!this.signalingUrl || !this.applicationName || !this.streamName) {
      return; // wait for required inputs
    }

    this.started = true;
    void this.start();
  }

  private tryRestart(): void {
    if (!this.started) {
      this.tryStart();
      return;
    }
    if (this.restarting) return;
    this.restarting = true;

    // Small debounce-style restart (handles multiple input changes in one cycle)
    queueMicrotask(() => {
      this.restarting = false;
      void this.start(); // start() stops existing first
    });
  }

  private async start(): Promise<void> {
    this.stop();

    // Validate inputs
    const signalingUrl = (this.signalingUrl ?? '').trim();
    const applicationName = (this.applicationName ?? '').trim();
    const streamName = (this.streamName ?? '').trim();

    if (!signalingUrl || !applicationName || !streamName) return;

    // Create RTCPeerConnection
    // Wowza often works with ICE configured server-side; keep iceServers empty unless you need STUN/TURN.
    const userData = { iceServers: [] as any[] };

    this.pc = new RTCPeerConnection(userData);

    this.pc.ontrack = (event) => {
      const video = this.videoRef.nativeElement;
      if (event.streams && event.streams[0]) {
        video.srcObject = event.streams[0];
      }
    };

    // For debugging if you want:
    // this.pc.onconnectionstatechange = () => console.log('PC state:', this.pc?.connectionState);
    // this.pc.onicecandidate = (e) => { if (e.candidate) console.log('Local ICE:', e.candidate.candidate); };

    // Open WebSocket signaling
    this.ws = new WebSocket(signalingUrl);

    const streamInfo: WowzaStreamInfo = { applicationName, streamName };

    this.ws.onopen = () => {
      const payload: WowzaSignalRequest = {
        direction: 'play',
        command: 'getOffer',
        streamInfo,
        userData,
      };

      if (this.secureToken != null) {
        payload.secureToken = this.secureToken;
      }

      this.ws?.send(JSON.stringify(payload));
    };

    this.ws.onmessage = async (evt) => {
      try {
        const msg = JSON.parse(evt.data) as WowzaSignalResponse;

        if (Number(msg.status) !== 200) {
          // signaling error from Wowza
          this.stop();
          return;
        }

        // Session id from Wowza
        if (msg.streamInfo?.sessionId) {
          streamInfo.sessionId = msg.streamInfo.sessionId;
        }

        // SDP offer from Wowza -> set remote -> create answer -> sendResponse
        if (msg.sdp) {
          const pc = this.pc;
          if (!pc) return;

          await pc.setRemoteDescription(new RTCSessionDescription(msg.sdp));

          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);

          const resp: WowzaSignalRequest = {
            direction: 'play',
            command: 'sendResponse',
            streamInfo,
            sdp: answer,
            userData,
          };

          // Usually only needed on getOffer, but harmless if included consistently.
          if (this.secureToken != null) {
            resp.secureToken = this.secureToken;
          }

          this.ws?.send(JSON.stringify(resp));
        }

        // Remote ICE candidates from Wowza
        if (msg.iceCandidates?.length) {
          const pc = this.pc;
          if (!pc) return;

          for (const c of msg.iceCandidates) {
            await pc.addIceCandidate(new RTCIceCandidate(c));
          }
        }

        // Wowza commonly closes WS after sendResponse; we can close too.
        if (msg.command === 'sendResponse') {
          try {
            this.ws?.close();
          } catch {}
          this.ws = null;
        }
      } catch {
        // ignore malformed messages; stop to be safe
        this.stop();
      }
    };

    this.ws.onerror = () => {
      // Browsers don't provide much detail for WS errors
      this.stop();
    };

    this.ws.onclose = () => {
      // If WS closes after negotiation, that's OK; media can continue over ICE/UDP.
      // Don't stop here automatically.
    };
  }

  private stop(): void {
    // Close WS
    if (this.ws) {
      try {
        this.ws.onopen = null;
        this.ws.onmessage = null;
        this.ws.onerror = null;
        this.ws.onclose = null;
        this.ws.close();
      } catch {}
      this.ws = null;
    }

    // Close PC
    if (this.pc) {
      try {
        this.pc.ontrack = null;
        this.pc.onicecandidate = null;
        this.pc.onconnectionstatechange = null;
        this.pc.close();
      } catch {}
      this.pc = null;
    }

    // Clear video
    const video = this.videoRef?.nativeElement;
    if (video) {
      try {
        (video as any).srcObject = null;
      } catch {}
      try {
        video.removeAttribute('src');
        video.load();
      } catch {}
    }
  }
}